import { usePepperSupply } from '@/hooks/pepper-contract/usePepperSupply'
import { Stat } from '@/components/Stat'
import { useStaking2aTotalPepperStaked } from '@/hooks/staking2a-contract/useStaking2aTotalStaked'
import { usePepperTreasuryEthBalance } from '@/hooks/treasury/usePepperTreasuryEthBalance'
import { formatEther } from 'viem'
import { useCEXFundEthBalance } from '@/hooks/cex-fund/useCEXFundEthBalance'
import { getCEXFundBalanceAddress } from '@/config/get-cex-fund-address'
import { getPepperTreasuryBalanceAddress } from '@/config/get-pepper-treasury-address'
import { getStaking2aAddress } from '@/config/get-staking2a-address'
import { useChainId } from 'wagmi'
import { NetworkId } from '@/types/network'
import { cn } from '@/lib/utils'
import { useStaking2aAddress } from '@/hooks/staking2a-contract/useStaking2aContract'
import { useStakerStats } from '@/data-access/stakes/queries'
import { toBigInt } from '@/utils/math/bigint'

const ONE_TRILLION = 1_000_000_000_000n * 10n ** 18n
const ONE_QUADRILLION = 1_000_000_000_000_000n * 10n ** 18n
const ONE_HUNDRED_THOUSAND = 100_000n * 10n ** 18n

function TotalSupplyStat({ className }: { className?: string }) {
	const { totalSupply } = usePepperSupply()

	return (
		<Stat
			className={className}
			src="/images/total-supply.webp"
			label="PEPPER Supply"
			value={formatEther(totalSupply.data ?? ONE_QUADRILLION)}
		>
			<Stat.Image />
			<Stat.Label />
			<Stat.Number />
		</Stat>
	)
}

function BurntSupplyStat({ className }: { className?: string }) {
	const { totalSupply, maxSupply } = usePepperSupply()

	const burnt = maxSupply.data && totalSupply.data ? maxSupply.data - totalSupply.data : ONE_TRILLION

	return (
		<Stat className={className} src="/images/burn.webp" label="Burnt PEPPER" value={formatEther(burnt)}>
			<Stat.Image />
			<Stat.Label />
			<Stat.Number />
		</Stat>
	)
}

function StakedSupplyStat({ className }: { className?: string }) {
	const { data: userTotalStaked, isPending } = useStaking2aTotalPepperStaked()
	const staking2aAddress = useStaking2aAddress()

	return (
		<Stat
			className={className}
			src="/images/vault.webp"
			label="Staked PEPPER"
			value={formatEther(isPending ? ONE_TRILLION : (userTotalStaked as bigint))}
		>
			<Stat.Image />
			<div className={cn('flex items-center gap-2 mt-4 mb-1', className)}>
				<Stat.Label className="mb-0 mt-0" />
				<Stat.Tooltip
					className="bg-white w-[350px] whitespace-pre-line"
					content={`This is the total amount of staked PEPPER.\n\nAddress: ${staking2aAddress}`}
				>
					{/* Font family renders weirdly on mobile, adding a mb-1 class to offset the difference */}
					<img className="w-5 mb-1 md:mb-0" src="images/tooltip-icon.svg" alt="tooltip icon" />
				</Stat.Tooltip>
			</div>
			<Stat.Number />
		</Stat>
	)
}

function TreasuryStat({ className }: { className?: string }) {
	const { data, isPending } = usePepperTreasuryEthBalance()
	const chainId = useChainId()
	const pepperTreasuryAddress = getPepperTreasuryBalanceAddress(chainId as NetworkId)

	return (
		<Stat
			className={className}
			label="Pepper Inc. Treasury"
			src="/images/treasury.svg"
			value={formatEther(isPending ? ONE_HUNDRED_THOUSAND : (data?.value as bigint))}
		>
			<Stat.Image />
			<div className={cn('flex items-center gap-2 mt-4 mb-1', className)}>
				<Stat.Label className="mb-0 mt-0" />
				<Stat.Tooltip
					className="bg-white w-[350px] whitespace-pre-line"
					content={`These funds are managed by the PEPPER community through voting.\n\nAddress: ${pepperTreasuryAddress}`}
				>
					{/* Font family renders weirdly on mobile, adding a mb-1 class to offset the difference */}
					<img className="w-5 mb-1 md:mb-0" src="images/tooltip-icon.svg" alt="tooltip icon" />
				</Stat.Tooltip>
			</div>
			<Stat.Number suffix=" CHZ" />
		</Stat>
	)
}

function CEXFundStat({ className }: { className?: string }) {
	const cexFundBalanceInWei = useCEXFundEthBalance().data?.value ?? 0n
	const chainId = useChainId()
	const cexFundAddress = getCEXFundBalanceAddress(chainId as NetworkId)
	const { data: stakeDetails, isPending, isError } = useStakerStats(cexFundAddress)

	const stakedInWei = isPending || isError ? 0n : toBigInt(stakeDetails?.totalStaked)
	const totalBalanceInWei = cexFundBalanceInWei + stakedInWei

	return (
		<Stat className={className} label="CEX Fund" src="/images/cex-fund.svg" value={formatEther(totalBalanceInWei)}>
			<Stat.Image />
			<div className={cn('flex items-center gap-2 mt-4 mb-1', className)}>
				<Stat.Label className="mb-0 mt-0" />
				<Stat.Tooltip
					className="bg-white w-[350px]"
					content={`The CEX Fund will be used to secure a Tier 1 Centralized Exchange listing for PEPPER. If the funds raised are insufficient after 90 days, they will be returned to the PEPPER Treasury to be managed by the community. Fund Address: ${cexFundAddress}`}
				>
					{/* Font family renders weirdly on mobile, adding a mb-1 class to offset the difference */}
					<img className="w-5 mb-1 md:mb-0" src="images/tooltip-icon.svg" alt="tooltip icon" />
				</Stat.Tooltip>
			</div>
			<Stat.Number suffix=" CHZ" />
		</Stat>
	)
}

export { TotalSupplyStat, TreasuryStat, BurntSupplyStat, StakedSupplyStat, CEXFundStat }
